import React from 'react'
import { BarChart3, CheckCircle, PieChart, TrendingUp, RefreshCw, Download, Settings, AlertTriangle, Activity, Zap } from 'lucide-react'
import { useDashboardStore, useStrategyStats } from '../../store/dashboardStore'

export function SimpleOverview() {
  const { totalStrategies, totalSurvivors, survivalRate, bestDsr, meanDsr } = useStrategyStats()
  const connectionStatus = useDashboardStore((state) => state.connectionStatus)
  
  const handleRefresh = () => {
    window.location.reload()
  }
  
  return (
    <div className="min-h-screen bg-slate-950 text-white p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            Alpha Discovery System
          </h1>
          <p className="text-slate-400 text-lg">
            High-level performance metrics and system status
          </p>
        </div>

        {/* Main Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total Strategies Card */}
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-6 hover:bg-slate-800 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm font-medium">Total Strategies</p>
                <p className="text-white text-3xl font-bold">{totalStrategies.toLocaleString()}</p>
                <p className="text-slate-500 text-xs mt-1">Generated across all runs</p>
              </div>
              <BarChart3 className="h-10 w-10 text-blue-400" />
            </div>
          </div>

          {/* Survivors Card */}
          <div className="bg-gradient-to-br from-emerald-900 to-green-800 border border-green-700 rounded-lg p-6 hover:shadow-lg transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-200 text-sm font-medium">Survivors</p>
                <p className="text-white text-3xl font-bold">{totalSurvivors.toLocaleString()}</p>
                <p className="text-green-300 text-xs mt-1">Passed validation threshold</p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-400" />
            </div>
          </div>

          {/* Survival Rate Card */}
          <div className="bg-gradient-to-br from-purple-900 to-violet-800 border border-purple-700 rounded-lg p-6 hover:shadow-lg transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-200 text-sm font-medium">Survival Rate</p>
                <p className="text-white text-3xl font-bold">{(survivalRate * 100).toFixed(1)}%</p>
                <p className="text-purple-300 text-xs mt-1">Quality filter efficiency</p>
              </div>
              <PieChart className="h-10 w-10 text-purple-400" />
            </div>
          </div>

          {/* Best DSR Card */}
          <div className="bg-gradient-to-br from-orange-900 to-yellow-800 border border-orange-700 rounded-lg p-6 hover:shadow-lg transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-200 text-sm font-medium">Best DSR</p>
                <p className="text-white text-3xl font-bold">{bestDsr.toFixed(3)}</p>
                <p className="text-orange-300 text-xs mt-1">Peak deflated Sharpe ratio</p>
              </div>
              <TrendingUp className="h-10 w-10 text-orange-400" />
            </div>
          </div>
        </div>

        {/* Secondary Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* System Status */}
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <Activity className="h-6 w-6 text-blue-400" />
              <h3 className="text-xl font-semibold text-white">System Status</h3>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-slate-700">
                <span className="text-slate-400">Connection Status</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  connectionStatus === 'connected' 
                    ? 'bg-green-900 text-green-300' 
                    : connectionStatus === 'connecting'
                    ? 'bg-yellow-900 text-yellow-300'
                    : 'bg-red-900 text-red-300'
                }`}>
                  {connectionStatus}
                </span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-b border-slate-700">
                <span className="text-slate-400">Average DSR</span>
                <span className="text-green-400 font-semibold">{meanDsr.toFixed(4)}</span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-b border-slate-700">
                <span className="text-slate-400">Research Pipeline</span>
                <span className="px-3 py-1 bg-green-900 text-green-300 rounded-full text-xs font-medium">
                  Active
                </span>
              </div>
              
              <div className="flex justify-between items-center py-3">
                <span className="text-slate-400">Data Sync</span>
                <span className="px-3 py-1 bg-green-900 text-green-300 rounded-full text-xs font-medium">
                  Operational
                </span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <Zap className="h-6 w-6 text-purple-400" />
              <h3 className="text-xl font-semibold text-white">Quick Actions</h3>
            </div>
            
            <div className="space-y-3">
              <button 
                onClick={handleRefresh}
                className="w-full flex items-center gap-3 p-3 bg-slate-800 hover:bg-slate-700 rounded-lg border border-slate-600 transition-colors text-white"
              >
                <RefreshCw className="h-5 w-5" />
                <span>Refresh Data</span>
              </button>
              
              <button className="w-full flex items-center gap-3 p-3 bg-slate-800 hover:bg-slate-700 rounded-lg border border-slate-600 transition-colors text-white">
                <Download className="h-5 w-5" />
                <span>Export Reports</span>
              </button>
              
              <button className="w-full flex items-center gap-3 p-3 bg-slate-800 hover:bg-slate-700 rounded-lg border border-slate-600 transition-colors text-white">
                <Settings className="h-5 w-5" />
                <span>System Settings</span>
              </button>
            </div>
          </div>
        </div>

        {/* Warning Banner */}
        <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-yellow-400 font-semibold mb-2">
                Research Performance Disclaimer
              </h4>
              <p className="text-slate-300 leading-relaxed">
                All performance metrics shown are backtested results and may not be representative of future performance. 
                Strategies shown as "survivors" have passed initial statistical validation but require additional 
                out-of-sample testing before deployment.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}